import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Clock component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconClock extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M12.0005 2.60498C6.81175 2.60498 2.60547 6.81127 2.60547 12C2.60547 17.1887 6.81175 21.395 12.0005 21.395C17.1892 21.395 21.3955 17.1887 21.3955 12C21.3955 6.81127 17.1892 2.60498 12.0005 2.60498ZM1.10547 12C1.10547 5.98284 5.98333 1.10498 12.0005 1.10498C18.0176 1.10498 22.8955 5.98284 22.8955 12C22.8955 18.0171 18.0176 22.895 12.0005 22.895C5.98333 22.895 1.10547 18.0171 1.10547 12Z"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M12 7.34814C12.4142 7.34814 12.75 7.68393 12.75 8.09814V11.7224L16.5335 16.1315C16.8033 16.4459 16.7671 16.9194 16.4528 17.1891C16.1384 17.4589 15.6649 17.4227 15.3952 17.1084L11.4308 12.4885C11.3141 12.3525 11.25 12.1792 11.25 12.0001V8.09814C11.25 7.68393 11.5858 7.34814 12 7.34814Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-clock') === undefined) {
  customElements.define('cat-icon-clock', BlocksIconClock);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-clock': BlocksIconClock;
  }
}
